// Helper functions for job-related operations

export const hasMoveRequest = (job) => {
  return job && job.MoveRequestDateInitiated && !job.MoveRequestDateMovedUpOut;
};

export const hasBeenMovedUp = (job) => {
  return job && job.MoveRequestDateMovedUpOut;
};

export const getCalculatedMoveDirection = (job) => {
  if (!job || !job.MoveRequestDateMovedUpOut) {
    return null;
  }

  // If MoveUpOut is explicitly set, use it
  if (job.MoveUpOut) {
    return job.MoveUpOut;
  }

  // Calculate based on dates if available
  if (job.MoveRequestPrevDate && job.CurrentPlanShipDate) {
    const prevDate = new Date(job.MoveRequestPrevDate);
    const currentDate = new Date(job.CurrentPlanShipDate);
    
    if (currentDate < prevDate) {
      return "Up"; // Moved to earlier date
    } else if (currentDate > prevDate) {
      return "Out"; // Moved to later date
    }
  }

  return null; // Cannot determine direction
};

export const getMoveRequestIcon = (job) => {
  if (!job || !job.MoveRequestDateInitiated) {
    return null;
  }

  // If the job has been moved, get the direction
  if (job.MoveRequestDateMovedUpOut) {
    const direction = getCalculatedMoveDirection(job);
    
    if (direction === "Up") {
      return "bi bi-arrow-up-circle";
    } else if (direction === "Out") {
      return "bi bi-arrow-down-circle";
    }
  }
  
  // For pending move requests, check if MoveUpOut is specified
  if (!job.MoveRequestDateMovedUpOut && job.MoveUpOut) {
    if (job.MoveUpOut === "Up") {
      return "bi bi-arrow-up-circle";
    } else if (job.MoveUpOut === "Out") {
      return "bi bi-arrow-down-circle";
    }
  }

  // Default icon for pending move requests or when direction cannot be determined
  return "bi bi-arrow-right-circle";
};

export const getMoveRequestTooltipText = (job) => {
  if (!job || !job.MoveRequestDateInitiated) {
    return null;
  }

  // If the job has been moved, get the direction and provide appropriate text
  if (job.MoveRequestDateMovedUpOut) {
    const direction = getCalculatedMoveDirection(job);
    const isCalculated = !job.MoveUpOut; // If MoveUpOut is not set, it was calculated
    
    if (direction === "Up") {
      return `This job has been moved up${isCalculated ? ' (calculated)' : ''}`;
    } else if (direction === "Out") {
      return `This job has been moved out${isCalculated ? ' (calculated)' : ''}`;
    }
    
    // Fallback if direction cannot be determined
    return "This job has been moved";
  }

  // For pending move requests with specified direction
  if (!job.MoveRequestDateMovedUpOut && job.MoveUpOut) {
    if (job.MoveUpOut === "Up") {
      return "This job has an active move request (up)";
    } else if (job.MoveUpOut === "Out") {
      return "This job has an active move request (out)";
    }
  }

  return "This job has an active move request";
};

export const hasWitnessTest = (job) => {
    return job && job.WitnessTestRequired && job.WitnessTestRequired.toLowerCase() === "yes";
};

export const calculateWeekTotalDollarValue = (weekJobs, formatCurrency) => {
  const totalDollarValue = weekJobs.reduce((sum, job) => {
    const dollarValue = job.DollarValue;
    let numValue = 0;
    
    if (typeof dollarValue === "number") {
      numValue = dollarValue;
    } else if (typeof dollarValue === "string" && dollarValue.trim() !== "") {
      // Convert string to number, removing any currency formatting
      const cleaned = dollarValue.replace(/[^0-9.-]/g, '');
      numValue = parseFloat(cleaned);
      if (isNaN(numValue)) numValue = 0;
    }
    
    return sum + numValue;
  }, 0);
  
  return formatCurrency(totalDollarValue);
};

export const getWeekDateRange = (week, weekColumns, moment) => {
  if (week === "Unscheduled" || week === "Past Due") {
    return "";
  }
  
  const weekNumber = parseInt(week.replace("Week ", ""), 10);
  if (weekColumns[weekNumber - 1]) {
    const weekColumn = weekColumns[weekNumber - 1];
    const startDate = moment(weekColumn.startDate);
    const endDate = moment(weekColumn.endDate);
    return `(${weekColumn.isoWeekNum + 1}): ${startDate.format('MM/DD')} - ${endDate.format('MM/DD')}`;
  }
  
  return "";
};

/**
 * Check if a job has incomplete change order tasks
 * @param {number} jobId - The ProjectID to check
 * @param {Array} changeOrderDetails - Array of change order detail records
 * @returns {boolean} - True if there are any incomplete tasks
 */
export const hasIncompleteChangeOrderTasks = (jobId, changeOrderDetails) => {
  if (!changeOrderDetails || changeOrderDetails.length === 0) {
    return false;
  }

  const getValue = (record, pascalKey, camelKey) =>
    record?.[pascalKey] ?? record?.[camelKey];

  // Filter change order details for this job
  const jobChangeOrders = changeOrderDetails.filter(
    (detail) => String(getValue(detail, 'ProjectID', 'projectId')) === String(jobId)
  );

  // Check if any task is incomplete
  return jobChangeOrders.some((detail) => {
    // Check Mechanical tasks
    if (getValue(detail, 'ActionRequiredMechanical', 'actionRequiredMechanical') && !getValue(detail, 'MechanicalCompleted', 'mechanicalCompleted')) {
      return true;
    }
    // Check Electrical tasks
    if (getValue(detail, 'ActionRequiredElectrical', 'actionRequiredElectrical') && !getValue(detail, 'ElectricalCompleted', 'electricalCompleted')) {
      return true;
    }
    // Check Controls tasks
    if (getValue(detail, 'ActionRequiredControls', 'actionRequiredControls') && !getValue(detail, 'ControlsCompleted', 'controlsCompleted')) {
      return true;
    }
    // Check CSM tasks
    if (getValue(detail, 'ActionRequiredCSM', 'actionRequiredCSM') && !getValue(detail, 'CSMCompleted', 'csmCompleted')) {
      return true;
    }
    return false;
  });
};
